/**
 * WhatsAppBiometricPanel — Platform Settings panel
 * Add to PlatformSettings.js:
 *   import WhatsAppBiometricPanel from './WhatsAppBiometricPanel';
 *   <WhatsAppBiometricPanel />
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { MessageCircle, Cpu, Eye, EyeOff, Send, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const WhatsAppBiometricPanel = () => {
    const [waForm,  setWA]      = useState({ whatsapp_enabled:false, whatsapp_provider:'msg91', msg91_auth_key:'', msg91_sender_id:'', msg91_template_id_base:'', twilio_account_sid:'', twilio_auth_token:'', twilio_whatsapp_from:'' });
    const [bioForm, setBio]     = useState({ biometric_enabled:false, biometric_provider:'zkteco', biometric_secret_key:'', biometric_device_model:'', biometric_late_threshold_minutes:15 });
    const [loading, setLoading] = useState(true);
    const [waSaving,  setWASave]  = useState(false);
    const [bioSaving, setBioSave] = useState(false);
    const [waTesting, setWATest]  = useState(false);
    const [testWATo,  setTestWATo] = useState('');
    const [showKey,  setShowKey]  = useState(false);
    const [showSecret, setShowSecret] = useState(false);

    useEffect(() => {
        Promise.all([
            axiosInstance.get('/superadmin/whatsapp-settings'),
            axiosInstance.get('/superadmin/biometric-settings'),
        ]).then(([wa, bio]) => {
            const wd = wa.data.data  || {};
            const bd = bio.data.data || {};
            setWA(p  => ({ ...p, ...wd,  whatsapp_enabled:  wd.whatsapp_enabled  !== false }));
            setBio(p => ({ ...p, ...bd,  biometric_enabled: bd.biometric_enabled !== false,
                biometric_late_threshold_minutes: parseInt(bd.biometric_late_threshold_minutes)||15 }));
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const saveWA = async () => {
        setWASave(true);
        try {
            await axiosInstance.put('/superadmin/whatsapp-settings', waForm);
            toast.success('WhatsApp settings saved!');
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setWASave(false); }
    };

    const saveBio = async () => {
        setBioSave(true);
        try {
            await axiosInstance.put('/superadmin/biometric-settings', bioForm);
            toast.success('Biometric settings saved!');
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setBioSave(false); }
    };

    const testWA = async () => {
        if (!testWATo.trim()) { toast.error('Enter a phone number (e.g. 919876543210)'); return; }
        setWATest(true);
        try {
            await axiosInstance.post('/superadmin/whatsapp-settings/test', { test_to: testWATo });
            toast.success(`Test WhatsApp sent to ${testWATo} ✓`);
        } catch (err) { toast.error(err.response?.data?.message || 'Test failed'); }
        finally { setWATest(false); }
    };

    const setW = (k, v) => setWA(p => ({ ...p, [k]: v }));
    const setB = (k, v) => setBio(p => ({ ...p, [k]: v }));

    if (loading) return <div className="text-center py-4"><Spinner animation="border" size="sm"/></div>;

    return (
        <div>
            {/* ═══ WHATSAPP ═══ */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <MessageCircle size={20} className="text-success"/>WhatsApp Notifications
                    </h5>
                    <p className="text-muted small mb-0">Send leave approvals, payslips, WFH approvals via WhatsApp</p>
                </div>
                <Form.Check type="switch" checked={!!waForm.whatsapp_enabled}
                    label={<span className="fw-bold">{waForm.whatsapp_enabled ? 'Enabled' : 'Disabled'}</span>}
                    onChange={e => setW('whatsapp_enabled', e.target.checked)}/>
            </div>

            {/* Provider cards */}
            <Row className="g-3 mb-4">
                {[
                    { key:'msg91',  label:'MSG91',  desc:'India-first, best for Indian numbers. Free 50 messages.', badge:'Recommended for India' },
                    { key:'twilio', label:'Twilio',  desc:'Global WhatsApp API. Works with WhatsApp Business.', badge:'International' },
                ].map(p => (
                    <Col key={p.key} xs={12} md={6}>
                        <div onClick={() => setW('whatsapp_provider', p.key)}
                            style={{ border:`2px solid ${waForm.whatsapp_provider===p.key?'#059669':'#e5e7eb'}`,
                                borderRadius:12, padding:'16px 20px', cursor:'pointer',
                                background: waForm.whatsapp_provider===p.key ? '#f0fdf4' : '#fff', transition:'all 0.2s' }}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <div className="fw-bold">{p.label}</div>
                                    <div className="text-muted small">{p.desc}</div>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <Badge bg="success" style={{ fontSize:10 }}>{p.badge}</Badge>
                                    {waForm.whatsapp_provider===p.key && <CheckCircle size={16} color="#059669"/>}
                                </div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>

            <Card className="border-0 shadow-sm p-4 mb-4">
                {waForm.whatsapp_provider === 'msg91' ? (
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Auth Key <span className="text-danger">*</span></Form.Label>
                            <InputGroup>
                                <Form.Control type={showKey?'text':'password'} value={waForm.msg91_auth_key}
                                    placeholder="MSG91 auth key from msg91.com"
                                    onChange={e => setW('msg91_auth_key', e.target.value)}/>
                                <Button variant="outline-secondary" onClick={() => setShowKey(p=>!p)} type="button">
                                    {showKey ? <EyeOff size={14}/> : <Eye size={14}/>}
                                </Button>
                            </InputGroup>
                            <Form.Text>Get from <a href="https://msg91.com/whatsapp" target="_blank" rel="noopener noreferrer">msg91.com/whatsapp</a></Form.Text>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Sender ID</Form.Label>
                            <Form.Control value={waForm.msg91_sender_id} placeholder="PEOPLE"
                                onChange={e => setW('msg91_sender_id', e.target.value)}/>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Default Template ID</Form.Label>
                            <Form.Control value={waForm.msg91_template_id_base} placeholder="MSG91 template ID"
                                onChange={e => setW('msg91_template_id_base', e.target.value)}/>
                        </Col>
                    </Row>
                ) : (
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Account SID <span className="text-danger">*</span></Form.Label>
                            <Form.Control value={waForm.twilio_account_sid} placeholder="ACxxxxxxxxxxxxxxxx"
                                onChange={e => setW('twilio_account_sid', e.target.value)}/>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Auth Token <span className="text-danger">*</span></Form.Label>
                            <InputGroup>
                                <Form.Control type={showKey?'text':'password'} value={waForm.twilio_auth_token}
                                    onChange={e => setW('twilio_auth_token', e.target.value)}/>
                                <Button variant="outline-secondary" onClick={() => setShowKey(p=>!p)} type="button">
                                    {showKey ? <EyeOff size={14}/> : <Eye size={14}/>}
                                </Button>
                            </InputGroup>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">From Number</Form.Label>
                            <Form.Control value={waForm.twilio_whatsapp_from} placeholder="whatsapp:+14155238886"
                                onChange={e => setW('twilio_whatsapp_from', e.target.value)}/>
                        </Col>
                    </Row>
                )}
            </Card>

            <div className="d-flex gap-3 flex-wrap mb-5">
                <Button variant="success" onClick={saveWA} disabled={waSaving}>
                    {waSaving ? <Spinner size="sm"/> : '💾 Save WhatsApp Settings'}
                </Button>
                <div className="d-flex gap-2 ms-auto align-items-center">
                    <Form.Control size="sm" placeholder="919876543210" style={{ width:200 }}
                        value={testWATo} onChange={e => setTestWATo(e.target.value)}/>
                    <Button variant="outline-success" size="sm" onClick={testWA} disabled={waTesting}>
                        {waTesting ? <Spinner size="sm"/> : <><Send size={13} className="me-1"/>Test</>}
                    </Button>
                </div>
            </div>

            <hr className="my-4"/>

            {/* ═══ BIOMETRIC ═══ */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Cpu size={20} className="text-primary"/>Biometric Attendance Sync
                    </h5>
                    <p className="text-muted small mb-0">Sync punch data from ZKTeco/Essl fingerprint devices</p>
                </div>
                <Form.Check type="switch" checked={!!bioForm.biometric_enabled}
                    label={<span className="fw-bold">{bioForm.biometric_enabled ? 'Enabled' : 'Disabled'}</span>}
                    onChange={e => setB('biometric_enabled', e.target.checked)}/>
            </div>

            <Card className="border-0 shadow-sm p-4 mb-4">
                <Row className="g-3">
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Device Brand</Form.Label>
                        <Form.Select value={bioForm.biometric_provider}
                            onChange={e => setB('biometric_provider', e.target.value)}>
                            <option value="zkteco">ZKTeco</option>
                            <option value="essl">eSSL (Realtime)</option>
                            <option value="mantra">Mantra</option>
                            <option value="secureye">Secureye</option>
                            <option value="other">Other</option>
                        </Form.Select>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Device Model</Form.Label>
                        <Form.Control value={bioForm.biometric_device_model} placeholder="e.g. ZKTeco K40"
                            onChange={e => setB('biometric_device_model', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Secret Key <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                            <Form.Control type={showSecret?'text':'password'} value={bioForm.biometric_secret_key}
                                placeholder="Set a strong secret key for your local agent"
                                onChange={e => setB('biometric_secret_key', e.target.value)}/>
                            <Button variant="outline-secondary" onClick={() => setShowSecret(p=>!p)} type="button">
                                {showSecret ? <EyeOff size={14}/> : <Eye size={14}/>}
                            </Button>
                        </InputGroup>
                        <Form.Text className="text-muted">
                            This key is used by the local PC agent to authenticate with PeoplesDock
                        </Form.Text>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Late Threshold (minutes after 9:00 AM)</Form.Label>
                        <Form.Control type="number" min={0} max={120}
                            value={bioForm.biometric_late_threshold_minutes}
                            onChange={e => setB('biometric_late_threshold_minutes', parseInt(e.target.value)||15)}/>
                    </Col>
                </Row>

                {bioForm.biometric_enabled && bioForm.biometric_secret_key && !bioForm.biometric_secret_key.startsWith('••••') && (
                    <Alert variant="info" className="mt-3 small">
                        <strong>Agent Endpoint:</strong><br/>
                        <code>POST {window.location.protocol}//{window.location.hostname}:5000/api/v1/attendance/biometric-sync</code><br/>
                        <strong>Payload:</strong> <code>{`{ "secret_key": "your-key", "company_id": YOUR_ID, "punches": [...] }`}</code>
                    </Alert>
                )}
            </Card>

            <Button variant="primary" onClick={saveBio} disabled={bioSaving}>
                {bioSaving ? <Spinner size="sm"/> : '💾 Save Biometric Settings'}
            </Button>
        </div>
    );
};

export default WhatsAppBiometricPanel;
